import React, { useState } from "react";
import {
  Button,
  Container,
  Divider,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from "@mui/material";
import BackupIcon from "@mui/icons-material/Backup";
import DownloadIcon from "@mui/icons-material/Download";
import RestoreIcon from "@mui/icons-material/Restore";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { toast } from "react-toastify";
import useFetchObjects from "../../../../api/useFetchObjects";
import ErrorPage from "../../../../Components/GeneralComponents/errorPage/ErrorPage";
import { useAuth } from "../../../../context";
import { API_URL } from "../../../../config";
import Loading from "../Asset/Loading";

const formatBytes = (bytes) => {
  const size = Number(bytes) || 0;
  if (size === 0) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  const index = Math.min(
    Math.floor(Math.log(size) / Math.log(1024)),
    units.length - 1,
  );
  return `${(size / Math.pow(1024, index)).toFixed(1)} ${units[index]}`;
};

const Backup = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [downloading, setDownloading] = useState("");
  const [restoring, setRestoring] = useState(false);
  const [restoreTarget, setRestoreTarget] = useState(null);

  const { data, isLoading, isError } = useFetchObjects(
    ["database-backup-list"],
    "database-backup/list",
    token,
  );

  const backups = data?.results || [];
  const headers = token ? { Authorization: `Token ${token}` } : {};

  const refreshList = () => {
    queryClient.invalidateQueries({ queryKey: ["database-backup-list"] });
  };

  const handleCreateBackup = async () => {
    setCreating(true);
    try {
      await axios.post(`${API_URL}/database-backup/backup/`, {}, { headers });
      toast.success(t("hr.backupCreated"));
      refreshList();
    } catch (error) {
      toast.error(error.response?.data?.error || t("hr.backupFailed"));
    } finally {
      setCreating(false);
    }
  };

  const handleDownload = async (fileName) => {
    setDownloading(fileName);
    try {
      const response = await axios.get(
        `${API_URL}/database-backup/download/${encodeURIComponent(fileName)}/`,
        { headers, responseType: "blob" },
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(t("hr.backupFailed"));
    } finally {
      setDownloading("");
    }
  };

  const handleRestore = async () => {
    if (!restoreTarget) return;
    setRestoring(true);
    try {
      await axios.post(
        `${API_URL}/database-backup/restore/`,
        { file_name: restoreTarget },
        { headers },
      );
      toast.success(t("hr.database_restored"));
      setRestoreTarget(null);
      refreshList();
    } catch (error) {
      toast.error(error.response?.data?.error || t("hr.restoreFailed"));
    } finally {
      setRestoring(false);
    }
  };

  if (isLoading) {
    return <Loading />;
  }

  if (isError) {
    return (
      <Grid container>
        <ErrorPage />
      </Grid>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ marginTop: 4 }}>
      <Paper elevation={3} sx={{ padding: 4, borderRadius: 2 }}>
        <Grid container alignItems="center" justifyContent="space-between">
          <Typography variant="h4" color="primary" fontWeight={700}>
            {t("hr.backup_management")}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={
              creating ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <BackupIcon style={{ margin: "0px 5px" }} />
              )
            }
            onClick={handleCreateBackup}
            disabled={creating}
          >
            {t("hr.create_backup")}
          </Button>
        </Grid>

        <Divider sx={{ my: 2 }} />

        <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
          {t("hr.backup_files")}
        </Typography>

        <TableContainer>
          <Table>
            <TableHead sx={{ bgcolor: "#ebe8e1" }}>
              <TableRow>
                <TableCell sx={{ fontWeight: "bold" }}>{t("hr.no")}</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>{t("hr.name")}</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>{t("hr.date")}</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>{t("hr.size")}</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>
                  {t("hr.action")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {backups.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    {t("hr.no_backup_files")}
                  </TableCell>
                </TableRow>
              ) : (
                backups.map((item, index) => (
                  <TableRow
                    key={item.name}
                    sx={{
                      backgroundColor:
                        index % 2 === 0 ? "action.hover" : "background.default",
                    }}
                  >
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>{item.name}</TableCell>
                    <TableCell>{item.modified_time}</TableCell>
                    <TableCell>{formatBytes(item.size)}</TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        startIcon={
                          downloading === item.name ? (
                            <CircularProgress size={16} />
                          ) : (
                            <DownloadIcon />
                          )
                        }
                        onClick={() => handleDownload(item.name)}
                        disabled={Boolean(downloading) || restoring}
                        sx={{ mr: 1 }}
                      >
                        {t("hr.download")}
                      </Button>
                      <Button
                        size="small"
                        color="warning"
                        startIcon={<RestoreIcon />}
                        onClick={() => setRestoreTarget(item.name)}
                        disabled={creating || restoring}
                      >
                        {t("hr.restore")}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog
        open={Boolean(restoreTarget)}
        onClose={() => !restoring && setRestoreTarget(null)}
      >
        <DialogTitle>{t("hr.confirmRestore")}</DialogTitle>
        <DialogContent>
          <Typography>{t("hr.restoreWarning")}</Typography>
          <Typography sx={{ mt: 1 }} fontWeight={600}>
            {restoreTarget}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRestoreTarget(null)} disabled={restoring}>
            {t("hr.cancel")}
          </Button>
          <Button
            color="warning"
            variant="contained"
            onClick={handleRestore}
            disabled={restoring}
            startIcon={
              restoring ? <CircularProgress size={16} color="inherit" /> : null
            }
          >
            {t("hr.restore")}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Backup;
